import React from 'react';
import { Translate } from 'react-localize-redux';
import {
    Box, Button, Card, CardContent, Chip, Grid, Stack, Typography,
} from '@mui/material';
import {
    WarningAmberRounded, PhoneOutlined, HomeOutlined, BadgeOutlined,
    LocalHospitalOutlined, CakeOutlined, CreditCardOutlined,
} from '@mui/icons-material';
import { green, orange } from '@mui/material/colors';
import Modal from '../../../components/general/modal';
import { DedupCandidate, DedupReason, normalizePhone } from '../../../utils/patientDedup';
import { resolvePersonalFieldRoles, PersonalFieldRoles } from '../../../utils/personalFieldRoles';
import { birthdateToDate, formatBirthdateForDisplay, yearsFromDate } from '../../../utils/birthdate';

// SHER-257: warning modal for the Add Patient flow. Shown when the entered
// details collide with one or more existing patients (health ID, name+DOB, or
// any phone field). It is a WARNING, not a hard block — "Create anyway" always
// lets the user proceed. The redesign (per the ticket mockup) lists every
// matching patient as a card and badges the criteria that fired.

interface PersonalField {
    name: string;
    label?: string;
    type?: string;
}

interface Props {
    open: boolean;
    matches: DedupCandidate[];
    reasons: DedupReason[];
    /** The investigation's personal-field config — used to resolve phone/address/clinician fields. */
    personalFields: PersonalField[];
    onGoBack: () => void;
    onReviewPatient: (uuid: string) => void;
    onCreateAnyway: () => void;
    closeModal: () => void;
}

const CRITERION_ICON: Record<DedupReason, React.ReactNode> = {
    health_id: <BadgeOutlined fontSize="small" />,
    name_dob: <CakeOutlined fontSize="small" />,
    name_dob_approx: <CakeOutlined fontSize="small" />,
    phone: <PhoneOutlined fontSize="small" />,
    id_card: <CreditCardOutlined fontSize="small" />,
};

function DetailRow({ icon, value }: { icon: React.ReactNode; value?: unknown }) {
    // Personal-data fields are strings post-decrypt, but coerce defensively so a
    // stray non-string value never throws "Objects are not valid as a React child".
    const text = value == null || value === '' ? '' : String(value);
    if (!text) return null;
    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
            {icon}
            <Typography variant="body2">{text}</Typography>
        </Stack>
    );
}

function PatientCard({
    patient, fieldNames, onReviewPatient,
}: {
    patient: DedupCandidate;
    fieldNames: PersonalFieldRoles;
    onReviewPatient: (uuid: string) => void;
}) {
    const pd = patient.personalData || {};
    const fullName = [pd.name, pd.surnames].filter(Boolean).join(' ').trim();
    const dob = formatBirthdateForDisplay(pd.birthdate, 'en');
    const age = yearsFromDate(birthdateToDate(pd.birthdate));
    const phoneValue = fieldNames.phone
        .map((n) => pd[n])
        .find((v) => normalizePhone(v));
    const address = fieldNames.address ? pd[fieldNames.address] : undefined;
    const clinician = fieldNames.clinician ? pd[fieldNames.clinician] : undefined;

    return (
        <Card variant="outlined" sx={{ width: '100%', borderColor: orange[200], borderRadius: 2 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={8}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {fullName || <Translate id="investigation.create.personal_data.fields.name" />}
                        </Typography>
                        {dob && (
                            <Typography variant="body2" color="text.secondary">
                                {dob}
                                {age != null && (
                                    <> · {age} <Translate id="pages.hospital.confirm-patient.years-old" /></>
                                )}
                            </Typography>
                        )}
                        <Stack spacing={0.5} mt={1}>
                            <DetailRow icon={<HomeOutlined fontSize="small" />} value={address} />
                            <DetailRow icon={<PhoneOutlined fontSize="small" />} value={phoneValue} />
                            <DetailRow icon={<LocalHospitalOutlined fontSize="small" />} value={clinician} />
                        </Stack>
                    </Grid>
                    <Grid item xs={12} sm={4} sx={{ textAlign: { sm: 'right' } }}>
                        <Button
                            size="small"
                            variant="text"
                            data-testid="duplicate-review-records"
                            onClick={() => onReviewPatient(patient.uuid)}
                        >
                            <Translate id="pages.hospital.confirm-patient.review-records" />
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

export default function DuplicatePatientModal(props: Props) {
    const { matches, reasons, personalFields } = props;
    const fieldNames = React.useMemo(() => resolvePersonalFieldRoles(personalFields), [personalFields]);

    return (
        <Modal
            open={props.open}
            maxWidth="md"
            fullWidth
            closeModal={props.closeModal}
            title={(
                <Stack direction="row" spacing={1} alignItems="center">
                    <WarningAmberRounded sx={{ color: orange[600] }} />
                    <Translate id="pages.hospital.confirm-patient.title" />
                </Stack>
            )}
        >
            <Box sx={{ width: '100%' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    <Translate id="pages.hospital.confirm-patient.description" />
                </Typography>

                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    <Translate id="pages.hospital.confirm-patient.criteria-detected" />
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5, mb: 2 }}>
                    {reasons.map((reason) => (
                        <Chip
                            key={reason}
                            size="small"
                            data-testid={`duplicate-criterion-${reason}`}
                            icon={CRITERION_ICON[reason] as React.ReactElement}
                            label={<Translate id={`pages.hospital.confirm-patient.criterion.${reason}`} />}
                            sx={{ backgroundColor: orange[50], color: orange[900] }}
                        />
                    ))}
                </Stack>

                <Stack spacing={1.5}>
                    {matches.map((patient) => (
                        <PatientCard
                            key={patient.uuid}
                            patient={patient}
                            fieldNames={fieldNames}
                            onReviewPatient={props.onReviewPatient}
                        />
                    ))}
                </Stack>

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    sx={{ mt: 3 }}
                >
                    <Button variant="outlined" data-testid="duplicate-go-back" onClick={props.onGoBack}>
                        <Translate id="pages.hospital.confirm-patient.go-back" />
                    </Button>
                    <Button
                        variant="contained"
                        data-testid="duplicate-create-anyway"
                        sx={{ backgroundColor: green[600], '&:hover': { backgroundColor: green[700] } }}
                        onClick={props.onCreateAnyway}
                    >
                        <Translate id="pages.hospital.confirm-patient.create-anyway" />
                    </Button>
                </Stack>
            </Box>
        </Modal>
    );
}
